// Tablo satırları için yerel HTML5 sürükle-bırak (Finar rev-2 #4b/#12b/#14 — Katalog + Veri
// Yönetimi ağacı). Yalnız AYNI üstteki satıra bırakılır: farklı üstte `dragover` engellenmez
// (preventDefault yok → tarayıcı "izin yok" imleci gösterir). Bırakma göstergesi hedef satırın
// üstünde/altında 2 px çizgi — `data-drop-position` niteliği, stil `globals.css`
// `tr[data-drop-position]`. Sürüklenebilir satır `aria-describedby` ile gizli ipucuna bağlanır
// ("Sürükleyin ya da ↑↓ ile taşıyın"); klavye eşdeğeri satırdaki ↑↓ / tutamaç tuşlarıdır.
// Dokunmatik cihazlarda özel işlem yok (↑↓ kalır). `enabled` false iken `rowProps` null döner —
// satır biçimlemesi sürükleme yokken birebir aynı kalır.

import { useEffect, useId, useRef, useState, type DragEvent } from "react";
import type { DropPosition } from "../lib/kpi-reorder";

/** İmleç satırın üst yarısındaysa önüne, alt yarısındaysa arkasına. */
export function positionFor(clientY: number, rect: { top: number; height: number }): DropPosition {
  return clientY < rect.top + rect.height / 2 ? "before" : "after";
}

/**
 * Bırakma kabul edilir mi: sürüklenen var, hedef kendisi değil, ikisinin üstü bilinir ve aynı.
 * `parentOf` bilinmeyen satırda `undefined` döner (kök = null).
 */
export function dropAllowed(
  draggedId: string | null,
  overId: string,
  parentOf: (id: string) => string | null | undefined,
): boolean {
  if (draggedId === null || draggedId === overId) return false;
  const a = parentOf(draggedId);
  return a !== undefined && a === parentOf(overId);
}

/**
 * Belge düzeyi `dragend`/`drop`: sürüklenen satır sürükleme sırasında DOM'dan düşse bile (yeniden
 * yükleme / süzgeç — kendi `dragend`i hiç ateşlenmez) takılı `dragged` kimliği temizlenir.
 * Sürükleme yoksa dokunulmaz. (Katalog sanal penceresi 2026-11-03'te kalktı; kural genel kalır.)
 */
export function shouldResetOnGlobalDragEnd(draggedId: string | null): boolean {
  return draggedId !== null;
}

export interface RowDragState {
  draggedId: string | null;
  overId: string | null;
  position: DropPosition | null;
}

export interface RowDragProps {
  draggable: true;
  "aria-describedby"?: string;
  "data-drop-position"?: DropPosition;
  onDragStart: (e: DragEvent<HTMLElement>) => void;
  onDragOver: (e: DragEvent<HTMLElement>) => void;
  onDragLeave: (e: DragEvent<HTMLElement>) => void;
  onDrop: (e: DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
}

export interface UseRowDragOptions {
  enabled: boolean;
  /** satırın üstü (kök = null); bilinmeyen satır undefined */
  parentOf: (id: string) => string | null | undefined;
  /** satır sürüklenebilir mi (ör. yetki); verilmezse hepsi */
  canDrag?: ((id: string) => boolean) | undefined;
  /** aynı üst kuralına EK kısıt (ör. etkilenen kardeşlerin hepsi düzenlenebilir) */
  canDrop?: ((draggedId: string, overId: string, position: DropPosition) => boolean) | undefined;
  onDrop: (draggedId: string, overId: string, position: DropPosition) => void;
  /** satıra `aria-describedby` ipucu bağla (varsayılan true; katalogda ipucu tutamaçta) */
  describeRows?: boolean | undefined;
}

const IDLE: RowDragState = { draggedId: null, overId: null, position: null };

export function useRowDrag(opts: UseRowDragOptions): {
  hintId: string;
  dragState: RowDragState;
  rowProps: (id: string) => RowDragProps | null;
} {
  const hintId = useId();
  const [dragState, setDragState] = useState<RowDragState>(IDLE);
  // dragover sık tetiklenir; sürüklenen kimlik ref'te (state güncellemesi gecikebilir)
  const dragged = useRef<string | null>(null);

  const reset = (): void => {
    dragged.current = null;
    setDragState(IDLE);
  };

  useEffect(() => {
    const onEnd = (): void => {
      if (shouldResetOnGlobalDragEnd(dragged.current)) reset();
    };
    document.addEventListener("dragend", onEnd);
    document.addEventListener("drop", onEnd);
    return () => {
      document.removeEventListener("dragend", onEnd);
      document.removeEventListener("drop", onEnd);
    };
  }, []);

  const accepts = (overId: string, position: DropPosition): boolean =>
    dropAllowed(dragged.current, overId, opts.parentOf) &&
    (opts.canDrop?.(dragged.current!, overId, position) ?? true);

  const rowProps = (id: string): RowDragProps | null => {
    if (!opts.enabled || (opts.canDrag && !opts.canDrag(id))) return null;
    const over = dragState.overId === id ? dragState.position : null;
    return {
      draggable: true,
      ...(opts.describeRows === false ? {} : { "aria-describedby": hintId }),
      ...(over ? { "data-drop-position": over } : {}),
      onDragStart: (e) => {
        dragged.current = id;
        e.dataTransfer.effectAllowed = "move";
        // Firefox veri olmadan sürüklemeyi başlatmaz
        e.dataTransfer.setData("text/plain", id);
        setDragState({ draggedId: id, overId: null, position: null });
      },
      onDragOver: (e) => {
        const position = positionFor(e.clientY, e.currentTarget.getBoundingClientRect());
        if (!accepts(id, position)) {
          // reddedilen hedefte eski bırakma çizgisi kalmasın
          if (dragState.overId === id)
            setDragState((s) => ({ ...s, overId: null, position: null }));
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dragState.overId !== id || dragState.position !== position) {
          setDragState({ draggedId: dragged.current, overId: id, position });
        }
      },
      onDragLeave: (e) => {
        // alt öğeye geçiş satırdan çıkış sayılmaz (çizgi titremesin)
        const next = e.relatedTarget;
        if (next instanceof Node && e.currentTarget.contains(next)) return;
        setDragState((s) => (s.overId === id ? { ...s, overId: null, position: null } : s));
      },
      onDrop: (e) => {
        const from = dragged.current;
        const position = positionFor(e.clientY, e.currentTarget.getBoundingClientRect());
        if (from === null || !accepts(id, position)) {
          reset();
          return;
        }
        e.preventDefault();
        reset();
        opts.onDrop(from, id, position);
      },
      onDragEnd: reset,
    };
  };

  return { hintId, dragState, rowProps };
}
